import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Plus, MoreVertical, Pencil, Trash2, Copy } from 'lucide-react';
import { SelectionCategoryItem } from './SelectionCategoryItem';
import { SelectionCategoryPicker } from './SelectionCategoryPicker';
import type { ProjectTypeConfig, SelectionCategoryConfig } from '@/types/firestore';

interface ProjectTypeAccordionProps {
  projectTypes: ProjectTypeConfig[];
  onChange: (projectTypes: ProjectTypeConfig[]) => void;
  defaultOpenId?: string;
}

export function ProjectTypeAccordion({
  projectTypes,
  onChange,
  defaultOpenId,
}: ProjectTypeAccordionProps) {
  const [editingProjectType, setEditingProjectType] = useState<ProjectTypeConfig | null>(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [categoryPickerOpen, setCategoryPickerOpen] = useState(false);
  const [activeProjectTypeId, setActiveProjectTypeId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState({ label: '', description: '' });

  // Edit project type label/description
  const openEditDialog = (projectType: ProjectTypeConfig) => {
    setEditingProjectType(projectType);
    setEditForm({
      label: projectType.label,
      description: projectType.description || '',
    });
    setEditDialogOpen(true);
  };

  const saveProjectTypeEdit = () => {
    if (!editingProjectType || !editForm.label.trim()) return;

    const updated = projectTypes.map((pt) =>
      pt.id === editingProjectType.id
        ? { ...pt, label: editForm.label.trim(), description: editForm.description.trim() || undefined }
        : pt
    );
    onChange(updated);
    setEditDialogOpen(false);
    setEditingProjectType(null);
  };

  // Delete project type
  const deleteProjectType = (projectTypeId: string) => {
    onChange(projectTypes.filter((pt) => pt.id !== projectTypeId));
  };

  // Duplicate project type
  const duplicateProjectType = (projectType: ProjectTypeConfig) => {
    const newId = `${projectType.id}_copy_${Date.now()}`;
    const newProjectType: ProjectTypeConfig = {
      ...projectType,
      id: newId,
      label: `${projectType.label} (Copy)`,
      selectionCategories: projectType.selectionCategories?.map((cat) => ({ ...cat })),
    };
    onChange([...projectTypes, newProjectType]);
  };

  // Add category to project type
  const openCategoryPicker = (projectTypeId: string) => {
    setActiveProjectTypeId(projectTypeId);
    setCategoryPickerOpen(true);
  };

  const addCategoryToProjectType = (category: SelectionCategoryConfig) => {
    if (!activeProjectTypeId) return;

    const updated = projectTypes.map((pt) => {
      if (pt.id === activeProjectTypeId) {
        const existingCategories = pt.selectionCategories || [];
        return {
          ...pt,
          selectionCategories: [...existingCategories, category],
        };
      }
      return pt;
    });
    onChange(updated);
    setActiveProjectTypeId(null);
  };

  // Remove category from project type
  const removeCategoryFromProjectType = (projectTypeId: string, categoryId: string) => {
    const updated = projectTypes.map((pt) => {
      if (pt.id === projectTypeId) {
        return {
          ...pt,
          selectionCategories: (pt.selectionCategories || []).filter(
            (cat) => cat.id !== categoryId
          ),
        };
      }
      return pt;
    });
    onChange(updated);
  };

  // Get existing category IDs for a project type (to exclude from picker)
  const getExistingCategoryIds = (projectTypeId: string): string[] => {
    const pt = projectTypes.find((p) => p.id === projectTypeId);
    return pt?.selectionCategories?.map((cat) => cat.id) || [];
  };

  return (
    <>
      <Accordion
        type="single"
        collapsible
        className="w-full"
        defaultValue={defaultOpenId}
      >
        {projectTypes.map((projectType) => (
          <AccordionItem key={projectType.id} value={projectType.id}>
            <AccordionTrigger className="text-sm hover:no-underline">
              <div className="flex items-center gap-2 flex-1 text-left min-w-0">
                <div className="flex flex-col items-start min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="font-medium">{projectType.label}</span>
                    <Badge variant="outline" className="text-[10px]">
                      {projectType.selectionCategories?.length || 0} categories
                    </Badge>
                  </div>
                  {projectType.description && (
                    <span className="text-xs text-muted-foreground truncate max-w-full">
                      {projectType.description}
                    </span>
                  )}
                </div>
              </div>
              <DropdownMenu>
                <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                  <Button variant="ghost" size="icon" className="h-7 w-7 mr-2">
                    <MoreVertical className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => openEditDialog(projectType)}>
                    <Pencil className="h-4 w-4 mr-2" />
                    Edit Details
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => duplicateProjectType(projectType)}>
                    <Copy className="h-4 w-4 mr-2" />
                    Duplicate
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => deleteProjectType(projectType.id)}
                    className="text-destructive focus:text-destructive"
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    Delete
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </AccordionTrigger>
            <AccordionContent>
              <div className="space-y-3 pt-2 pb-1">
                <div className="space-y-2">
                  <Label className="text-xs text-muted-foreground">Selection Categories</Label>
                  {projectType.selectionCategories && projectType.selectionCategories.length > 0 ? (
                    <div className="space-y-1">
                      {projectType.selectionCategories.map((category) => (
                        <SelectionCategoryItem
                          key={category.id}
                          category={category}
                          onEdit={() => {
                            // TODO: Open category options editor
                            console.log('Edit category:', category.id);
                          }}
                          onDelete={() =>
                            removeCategoryFromProjectType(projectType.id, category.id)
                          }
                        />
                      ))}
                    </div>
                  ) : (
                    <p className="text-xs text-muted-foreground italic py-2">
                      No selection categories configured
                    </p>
                  )}

                  <Button
                    variant="outline"
                    size="sm"
                    className="gap-1 mt-2"
                    onClick={() => openCategoryPicker(projectType.id)}
                  >
                    <Plus className="h-3.5 w-3.5" />
                    Add Category
                  </Button>
                </div>
              </div>
            </AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>

      {/* Edit Project Type Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Project Type</DialogTitle>
            <DialogDescription>
              Update the label and description for this project type
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="pt-label">Label</Label>
              <Input
                id="pt-label"
                value={editForm.label}
                onChange={(e) => setEditForm((prev) => ({ ...prev, label: e.target.value }))}
                placeholder="Project type name"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="pt-description">Description</Label>
              <Input
                id="pt-description"
                value={editForm.description}
                onChange={(e) =>
                  setEditForm((prev) => ({ ...prev, description: e.target.value }))
                }
                placeholder="Brief description"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={saveProjectTypeEdit} disabled={!editForm.label.trim()}>
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Category Picker */}
      <SelectionCategoryPicker
        open={categoryPickerOpen}
        onOpenChange={setCategoryPickerOpen}
        onSelect={addCategoryToProjectType}
        excludeIds={activeProjectTypeId ? getExistingCategoryIds(activeProjectTypeId) : []}
      />
    </>
  );
}
